import { useContext, useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import type {
  ConversationTemplateContent,
  ConversationTemplate,
  UpdateConversationTemplateContent,
} from "@rubi/api-generated/clientRs";
import {
  deleteConversationTemplate,
  deleteConversationTemplateContent,
  getConversationClient,
  listConversationTemplate,
  patchConversationClient,
  patchConversationTemplate,
  postConversationTemplate,
  postConversationTemplateTranslate,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

import LanguageSelector, {
  SUPPORTED_LANGUAGES,
} from "@/common/LanguageSelector";
import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

import AddTemplateForm from "./AddTemplateForm";
import TemplateRow from "./TemplateRow";
import type { DraftState, TemplateRow as TemplateRowType } from "./types";

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    fontWeight={700}
    color="text.secondary"
    sx={{ letterSpacing: "0.17px", mb: 0.5 }}
  >
    {children}
  </Typography>
);

const normalizeSupportedLanguages = (languages: string[]) => {
  const normalized = ["en"];
  languages.forEach((language) => {
    const code = language.trim();
    if (code && !normalized.includes(code)) normalized.push(code);
  });
  return normalized;
};

const sameLanguages = (a: string[], b: string[]) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

const getLanguageName = (code: string) =>
  SUPPORTED_LANGUAGES.find((language) => language.code === code)?.name ?? code;

const toDraftContents = (
  contents: ConversationTemplateContent[] | null | undefined,
) =>
  (contents ?? []).map((c) => ({
    lang: c.lang,
    content: c.content,
    is_ai_translated: c.is_ai_translated,
    is_verified: c.is_verified,
  }));

const MessageSettings = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { t } = useTranslation("message");

  const { currentUser } = useContext(AuthStateContext);
  const currentUserPermissions =
    currentUser?.permissions?.map((p) => p.title as Permission) ?? [];
  const { canWriteMessageTemplate } = userPermissionChecks(
    currentUserPermissions,
  );

  if (!canWriteMessageTemplate) {
    return <Navigate to={`/rubimessage/${orgId}`} replace />;
  }

  const [rows, setRows] = useState<TemplateRowType[]>([]);
  const [autoReplyTemplate, setAutoReplyTemplate] =
    useState<ConversationTemplate | null>(null);
  const [oneWayTemplate, setOneWayTemplate] =
    useState<ConversationTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const [conversationClientId, setConversationClientId] = useState<
    string | null
  >(null);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [langDraft, setLangDraft] = useState<string[]>([]);
  const [langSaving, setLangSaving] = useState(false);
  const [languageConfirmOpen, setLanguageConfirmOpen] = useState(false);

  const [showAddNew, setShowAddNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");
  const [creating, setCreating] = useState(false);

  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [sortConfirmOpen, setSortConfirmOpen] = useState(false);

  const dragIndex = useRef<number | null>(null);
  const dragStartRowsRef = useRef<TemplateRowType[] | null>(null);
  const dragFinalRowsRef = useRef<TemplateRowType[] | null>(null);

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      try {
        setLoading(true);
        const [clientRes, templatesRes] = await Promise.all([
          getConversationClient({ query: { rubi_org_id: orgId } }),
          listConversationTemplate({ query: { rubi_org_id: orgId } }),
        ]);

        const client = clientRes.data ?? null;
        const allTemplates = templatesRes.data?.conversation_templates ?? [];
        const clientLanguages = normalizeSupportedLanguages(
          client?.supported_languages ?? ["en"],
        );

        setConversationClientId(client?.id ?? null);
        setSupportedLanguages(clientLanguages);
        setLangDraft(clientLanguages);

        setAutoReplyTemplate(
          allTemplates.find((t) => t.id === client?.auto_reply_template_id) ??
            null,
        );
        setOneWayTemplate(
          allTemplates.find(
            (t) => t.id === client?.one_way_notification_template_id,
          ) ?? null,
        );

        const specialIds = new Set([
          client?.auto_reply_template_id,
          client?.one_way_notification_template_id,
        ]);

        setRows(
          allTemplates
            .filter(
              (t) =>
                !t.is_managed_online_reporting &&
                !t.is_managed_registry &&
                !specialIds.has(t.id),
            )
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((t) => ({ template: t, enabled: !t.is_hidden })),
        );
      } catch {
        setError(t("settings.toast.templatesLoadFailed"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const handleSaveLanguages = async () => {
    if (!conversationClientId) return;
    const languages = normalizeSupportedLanguages(langDraft);
    try {
      setLangSaving(true);
      setLanguageConfirmOpen(false);
      const res = await patchConversationClient({
        path: { conversation_client_id: conversationClientId },
        body: { supported_languages: languages },
      });
      const updatedLanguages = normalizeSupportedLanguages(
        res.data?.supported_languages ?? languages,
      );
      setSupportedLanguages(updatedLanguages);
      setLangDraft(updatedLanguages);

      if (orgId && res.data) {
        const templatesRes = await listConversationTemplate({
          query: { rubi_org_id: orgId },
        });
        const allTemplates = templatesRes.data?.conversation_templates ?? [];
        const specialIds = new Set([
          res.data.auto_reply_template_id,
          res.data.one_way_notification_template_id,
        ]);

        setAutoReplyTemplate(
          allTemplates.find((t) => t.id === res.data?.auto_reply_template_id) ??
            null,
        );
        setOneWayTemplate(
          allTemplates.find(
            (t) => t.id === res.data?.one_way_notification_template_id,
          ) ?? null,
        );
        setRows(
          allTemplates
            .filter(
              (t) =>
                !t.is_managed_online_reporting &&
                !t.is_managed_registry &&
                !specialIds.has(t.id),
            )
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((t) => ({ template: t, enabled: !t.is_hidden })),
        );
        setDrafts((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((templateId) => {
            const template = allTemplates.find((t) => t.id === templateId);
            if (template) {
              next[templateId] = {
                ...next[templateId],
                contents: toDraftContents(template.content),
              };
            }
          });
          return next;
        });
      }

      toast.success(t("settings.toast.languagesSaved"));
    } catch {
      toast.error(t("settings.toast.languagesSaveFailed"));
    } finally {
      setLangSaving(false);
    }
  };

  const targetLanguageDraft = langDraft.filter((lang) => lang !== "en");
  const targetLanguageOptions = SUPPORTED_LANGUAGES.filter(
    (language) => language.code !== "en",
  );
  const normalizedLangDraft = normalizeSupportedLanguages(langDraft);
  const addedLanguageDraft = normalizedLangDraft.filter(
    (lang) => lang !== "en" && !supportedLanguages.includes(lang),
  );
  const removedLanguageDraft = supportedLanguages.filter(
    (lang) => lang !== "en" && !normalizedLangDraft.includes(lang),
  );
  const languageDraftChanged = !sameLanguages(
    normalizedLangDraft,
    supportedLanguages,
  );

  const toggleEnabled = async (id: string) => {
    if (id === autoReplyTemplate?.id) {
      const newEnabled = autoReplyTemplate.is_hidden;
      setAutoReplyTemplate(
        (prev) => prev && { ...prev, is_hidden: !newEnabled },
      );
      try {
        await patchConversationTemplate({
          path: { conversation_template_id: id },
          body: { is_hidden: !newEnabled },
        });
      } catch {
        setAutoReplyTemplate(
          (prev) => prev && { ...prev, is_hidden: newEnabled },
        );
        toast.error(t("settings.toast.updateFailed"));
      }
      return;
    }
    if (id === oneWayTemplate?.id) {
      const newEnabled = oneWayTemplate.is_hidden;
      setOneWayTemplate((prev) => prev && { ...prev, is_hidden: !newEnabled });
      try {
        await patchConversationTemplate({
          path: { conversation_template_id: id },
          body: { is_hidden: !newEnabled },
        });
      } catch {
        setOneWayTemplate((prev) => prev && { ...prev, is_hidden: newEnabled });
        toast.error(t("settings.toast.updateFailed"));
      }
      return;
    }
    const row = rows.find((r) => r.template.id === id);
    if (!row) return;
    const newEnabled = !row.enabled;
    setRows((prev) =>
      prev.map((r) =>
        r.template.id === id ? { ...r, enabled: newEnabled } : r,
      ),
    );
    try {
      await patchConversationTemplate({
        path: { conversation_template_id: id },
        body: { is_hidden: !newEnabled },
      });
    } catch {
      setRows((prev) =>
        prev.map((r) =>
          r.template.id === id ? { ...r, enabled: !newEnabled } : r,
        ),
      );
      toast.error(t("settings.toast.updateFailed"));
    }
  };

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
    dragStartRowsRef.current = rows;
    dragFinalRowsRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current!, 1);
      next.splice(index, 0, moved);
      dragIndex.current = index;
      dragFinalRowsRef.current = next;
      return next;
    });
  };

  const handleDragEnd = async () => {
    dragIndex.current = null;
    const previousRows = dragStartRowsRef.current;
    const finalRows = dragFinalRowsRef.current;
    dragStartRowsRef.current = null;
    dragFinalRowsRef.current = null;
    if (!finalRows) return;

    try {
      await persistSortOrder(finalRows);
    } catch {
      if (previousRows) setRows(previousRows);
      toast.error(t("settings.toast.orderSaveFailed"));
    }
  };

  const toggleEdit = (
    templateId: string,
    templateData: ConversationTemplate,
  ) => {
    if (expandedId === templateId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(templateId);
    setDrafts((prev) => ({
      ...prev,
      [templateId]: prev[templateId] ?? {
        name: templateData.name,
        contents: (templateData.content ?? []).map((c) => ({
          lang: c.lang,
          content: c.content,
          is_ai_translated: c.is_ai_translated,
          is_verified: c.is_verified,
        })),
        translationsOpen: false,
        expandedLang: null,
        addingLang: false,
        newLangCode: "",
        newLangContent: "",
      },
    }));
  };

  const updateDraft = (id: string, updates: Partial<DraftState>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...updates } }));
  };

  const updateDraftLangContent = (id: string, lang: string, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        contents: prev[id].contents.map((c) =>
          c.lang === lang ? { ...c, content: value } : c,
        ),
      },
    }));
  };

  const handleSave = async (templateId: string) => {
    const draft = drafts[templateId];
    if (!draft) return;

    const originalTemplate =
      rows.find((r) => r.template.id === templateId)?.template ??
      (autoReplyTemplate?.id === templateId ? autoReplyTemplate : null) ??
      (oneWayTemplate?.id === templateId ? oneWayTemplate : null);

    const originalName = originalTemplate?.name ?? "";
    const originalEnContent =
      originalTemplate?.content?.find((c) => c.lang === "en")?.content ?? "";
    const draftEnContent =
      draft.contents.find((c) => c.lang === "en")?.content ?? "";
    const englishChanged = draftEnContent !== originalEnContent;
    const nameChanged = draft.name !== originalName;

    if (!englishChanged && !nameChanged) return;

    try {
      setSaving(templateId);
      const res = await patchConversationTemplate({
        path: { conversation_template_id: templateId },
        body: { name: draft.name, content: draft.contents },
      });
      if (res.data) {
        setRows((prev) =>
          prev.map((r) =>
            r.template.id === res.data!.id ? { ...r, template: res.data! } : r,
          ),
        );
        if (res.data.id === autoReplyTemplate?.id)
          setAutoReplyTemplate(res.data);
        if (res.data.id === oneWayTemplate?.id) setOneWayTemplate(res.data);
      }

      // If English content changed, re-translate all AI-translated languages
      if (englishChanged) {
        const aiLangs = draft.contents
          .filter((c) => c.lang !== "en" && c.is_ai_translated)
          .map((c) => c.lang);
        if (aiLangs.length > 0) {
          const translateRes = await postConversationTemplateTranslate({
            path: { conversation_template_id: templateId },
            body: { langs: aiLangs },
          });
          if (translateRes.data) {
            const updatedContents = (translateRes.data.content ?? []).map(
              (c) => ({
                lang: c.lang,
                content: c.content,
                is_ai_translated: c.is_ai_translated,
                is_verified: c.is_verified,
              }),
            );
            setDrafts((prev) => ({
              ...prev,
              [templateId]: { ...prev[templateId], contents: updatedContents },
            }));
            setRows((prev) =>
              prev.map((r) =>
                r.template.id === translateRes.data!.id
                  ? { ...r, template: translateRes.data! }
                  : r,
              ),
            );
            if (translateRes.data.id === autoReplyTemplate?.id)
              setAutoReplyTemplate(translateRes.data);
            if (translateRes.data.id === oneWayTemplate?.id)
              setOneWayTemplate(translateRes.data);
          }
        }
      }
    } catch {
      toast.error(t("settings.toast.templateSaveFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateTranslation = async (templateId: string, lang: string) => {
    const draft = drafts[templateId];
    if (!draft) return;
    const draftContent =
      draft.contents.find((c) => c.lang === lang)?.content ?? "";
    const originalTemplate =
      rows.find((r) => r.template.id === templateId)?.template ??
      (autoReplyTemplate?.id === templateId ? autoReplyTemplate : null) ??
      (oneWayTemplate?.id === templateId ? oneWayTemplate : null);
    const originalContent =
      originalTemplate?.content?.find((c) => c.lang === lang)?.content ?? "";
    if (draftContent === originalContent) return;
    const updatedContents = draft.contents.map((c) =>
      c.lang === lang
        ? { ...c, is_ai_translated: false, is_verified: true }
        : c,
    );
    setDrafts((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        contents: updatedContents,
      },
    }));
    try {
      setSaving(templateId);
      const res = await patchConversationTemplate({
        path: { conversation_template_id: templateId },
        body: { content: updatedContents },
      });
      if (res.data) {
        setRows((prev) =>
          prev.map((r) =>
            r.template.id === res.data!.id ? { ...r, template: res.data! } : r,
          ),
        );
        if (res.data.id === autoReplyTemplate?.id)
          setAutoReplyTemplate(res.data);
        if (res.data.id === oneWayTemplate?.id) setOneWayTemplate(res.data);
      }
    } catch {
      toast.error(t("settings.toast.translationUpdateFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleAutoTranslate = async (templateId: string, lang: string) => {
    try {
      setSaving(templateId);
      const res = await postConversationTemplateTranslate({
        path: { conversation_template_id: templateId },
        body: { langs: [lang] },
      });
      if (res.error) {
        toast.error(
          t("settings.toast.translationFailed", {
            error: JSON.stringify(res.error),
          }),
        );
        return;
      }
      if (res.data) {
        const updatedContents = (res.data.content ?? []).map((c) => ({
          lang: c.lang,
          content: c.content,
          is_ai_translated: c.is_ai_translated,
          is_verified: c.is_verified,
        }));
        setDrafts((prev) => ({
          ...prev,
          [templateId]: {
            ...prev[templateId],
            contents: updatedContents,
            translationsOpen: true,
            expandedLang: lang,
            addingLang: false,
            newLangCode: "",
            newLangContent: "",
          },
        }));
        setRows((prev) =>
          prev.map((r) =>
            r.template.id === res.data!.id ? { ...r, template: res.data! } : r,
          ),
        );
        if (res.data.id === autoReplyTemplate?.id)
          setAutoReplyTemplate(res.data);
        if (res.data.id === oneWayTemplate?.id) setOneWayTemplate(res.data);
        toast.success(t("settings.toast.autoTranslationComplete"));
      }
    } catch {
      toast.error(t("settings.toast.autoTranslateFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      setSaving(templateId);
      await deleteConversationTemplate({
        path: { conversation_template_id: templateId },
      });
      setRows((prev) => prev.filter((r) => r.template.id !== templateId));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[templateId];
        return next;
      });
      if (expandedId === templateId) setExpandedId(null);
      toast.success(t("settings.toast.templateDeleted"));
    } catch {
      toast.error(t("settings.toast.templateDeleteFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteTranslation = async (templateId: string, lang: string) => {
    try {
      setSaving(templateId);
      const res = await deleteConversationTemplateContent({
        path: { conversation_template_id: templateId, lang },
      });
      if (res.data) {
        const updatedContents = toDraftContents(res.data.content);
        setDrafts((prev) => ({
          ...prev,
          [templateId]: {
            ...prev[templateId]!,
            contents: updatedContents,
            expandedLang: null,
            translationsOpen: true,
          },
        }));
        setRows((prev) =>
          prev.map((r) =>
            r.template.id === res.data!.id ? { ...r, template: res.data! } : r,
          ),
        );
        if (res.data.id === autoReplyTemplate?.id)
          setAutoReplyTemplate(res.data);
        if (res.data.id === oneWayTemplate?.id) setOneWayTemplate(res.data);
      }
      toast.success(t("settings.toast.translationDeleted"));
    } catch {
      toast.error(t("settings.toast.translationDeleteFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleAddLang = async (templateId: string) => {
    const draft = drafts[templateId];
    if (!draft || !draft.newLangCode.trim() || !draft.newLangContent.trim())
      return;
    const newEntry: UpdateConversationTemplateContent = {
      lang: draft.newLangCode.trim().toLowerCase(),
      content: draft.newLangContent,
      is_ai_translated: false,
      is_verified: true,
    };
    const updatedContents = [...draft.contents, newEntry];
    setDrafts((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        contents: updatedContents,
        addingLang: false,
        newLangCode: "",
        newLangContent: "",
        translationsOpen: true,
        expandedLang: newEntry.lang,
      },
    }));
    try {
      setSaving(templateId);
      const res = await patchConversationTemplate({
        path: { conversation_template_id: templateId },
        body: { content: updatedContents },
      });
      if (res.data) {
        setRows((prev) =>
          prev.map((r) =>
            r.template.id === res.data!.id ? { ...r, template: res.data! } : r,
          ),
        );
        if (res.data.id === autoReplyTemplate?.id)
          setAutoReplyTemplate(res.data);
        if (res.data.id === oneWayTemplate?.id) setOneWayTemplate(res.data);
      }
      toast.success(t("settings.toast.translationAdded"));
    } catch {
      toast.error(t("settings.toast.translationAddFailed"));
    } finally {
      setSaving(null);
    }
  };

  const handleSortAlphabetically = () => {
    if (rows.length <= 1) return;
    setSortConfirmOpen(true);
  };

  const persistSortOrder = async (nextRows: TemplateRowType[]) => {
    const patches = nextRows
      .map((row, index) => ({ row, newOrder: index }))
      .filter(({ row, newOrder }) => row.template.sort_order !== newOrder);
    if (patches.length > 0) {
      await Promise.all(
        patches.map(({ row, newOrder }) =>
          patchConversationTemplate({
            path: { conversation_template_id: row.template.id },
            body: { sort_order: newOrder },
          }),
        ),
      );
    }
    setRows(
      nextRows.map((row, index) => ({
        ...row,
        template: { ...row.template, sort_order: index },
      })),
    );
  };

  const applySortAlphabetically = async () => {
    setSortConfirmOpen(false);
    const sorted = [...rows].sort((a, b) =>
      a.template.name.localeCompare(b.template.name),
    );
    try {
      await persistSortOrder(sorted);
    } catch {
      toast.error(t("settings.toast.orderSaveFailed"));
    }
  };

  const handleCreate = async () => {
    if (!orgId || !newName.trim() || !newContent.trim()) return;
    setCreateConfirmOpen(false);
    try {
      setCreating(true);
      const res = await postConversationTemplate({
        body: {
          rubi_org_id: orgId,
          name: newName,
          content: newContent,
          lang: "en",
        },
      });
      if (res.data) {
        const newSortOrder =
          rows.length === 0
            ? 0
            : Math.max(...rows.map((r) => r.template.sort_order)) + 1;
        await patchConversationTemplate({
          path: { conversation_template_id: res.data.id },
          body: { sort_order: newSortOrder },
        });
        setRows((prev) => [
          ...prev,
          {
            template: { ...res.data!, sort_order: newSortOrder },
            enabled: true,
          },
        ]);
        toast.success(
          targetLanguageDraft.length > 0
            ? t("settings.toast.templateCreatedAndTranslated")
            : t("settings.toast.templateCreated"),
        );
        setShowAddNew(false);
        setNewName("");
        setNewContent("");
      }
    } catch {
      toast.error(t("settings.toast.templateCreateFailed"));
    } finally {
      setCreating(false);
    }
  };

  const makeRowProps = (template: ConversationTemplate, extraIndex = 0) => ({
    template,
    enabled: !template.is_hidden,
    index: extraIndex,
    isExpanded: expandedId === template.id,
    draft: drafts[template.id],
    isSaving: saving === template.id,
    translationLanguages: supportedLanguages,
    onDragStart: () => {},
    onDragOver: () => {},
    onDragEnd: () => {},
    onToggleEnabled: () => toggleEnabled(template.id),
    onToggleEdit: () => toggleEdit(template.id, template),
    onUpdateDraft: (updates: Partial<DraftState>) =>
      updateDraft(template.id, updates),
    onUpdateLangContent: (lang: string, value: string) =>
      updateDraftLangContent(template.id, lang, value),
    onUpdateTranslation: (lang: string) =>
      handleUpdateTranslation(template.id, lang),
    onAddLang: () => handleAddLang(template.id),
    onAutoTranslate: (lang: string) => handleAutoTranslate(template.id, lang),
    onDeleteLang: (lang: string) => handleDeleteTranslation(template.id, lang),
    onSave: () => handleSave(template.id),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        {t("settings.title")}
      </Typography>

      {/* Translation Languages section */}
      <Box sx={{ mb: 3 }}>
        <SectionLabel>{t("settings.translationLanguages")}</SectionLabel>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {t("settings.translationLanguagesDescription")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <LanguageSelector
            multiple
            value={targetLanguageDraft}
            onChange={(languages) =>
              setLangDraft(normalizeSupportedLanguages(languages))
            }
            columns={4}
            languages={targetLanguageOptions}
            triggerMinWidth={260}
          />
          {langSaving && (
            <Alert severity="info">{t("settings.updatingTranslations")}</Alert>
          )}
          <Box>
            <Button
              variant="contained"
              size="small"
              disabled={
                langSaving || !conversationClientId || !languageDraftChanged
              }
              onClick={() => setLanguageConfirmOpen(true)}
              sx={{ whiteSpace: "nowrap", minWidth: 64 }}
            >
              {langSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                t("settings.save")
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Automatic Messages section */}
      {(autoReplyTemplate || oneWayTemplate) && (
        <Box sx={{ mb: 3 }}>
          <SectionLabel>{t("settings.automaticMessages")}</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {autoReplyTemplate && (
              <TemplateRow
                {...makeRowProps(autoReplyTemplate)}
                isDraggable={false}
                description={t("settings.autoReplyDescription")}
              />
            )}
            {oneWayTemplate && (
              <TemplateRow
                {...makeRowProps(oneWayTemplate)}
                isDraggable={false}
                description={t("settings.oneWayDescription")}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Field Options section */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <SectionLabel>{t("settings.fieldOptions")}</SectionLabel>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSortAlphabetically}
          sx={{ mb: 0.5 }}
        >
          {t("settings.sortAlphabeticallyByTitle")}
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map(({ template, enabled }, index) => (
          <TemplateRow
            key={template.id}
            template={template}
            enabled={enabled}
            index={index}
            isExpanded={expandedId === template.id}
            draft={drafts[template.id]}
            isSaving={saving === template.id}
            translationLanguages={supportedLanguages}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onToggleEnabled={() => toggleEnabled(template.id)}
            onToggleEdit={() => toggleEdit(template.id, template)}
            onUpdateDraft={(updates) => updateDraft(template.id, updates)}
            onUpdateLangContent={(lang, value) =>
              updateDraftLangContent(template.id, lang, value)
            }
            onUpdateTranslation={(lang) =>
              handleUpdateTranslation(template.id, lang)
            }
            onAddLang={() => handleAddLang(template.id)}
            onAutoTranslate={(lang) => handleAutoTranslate(template.id, lang)}
            onDeleteLang={(lang) => handleDeleteTranslation(template.id, lang)}
            onDeleteTemplate={() => handleDeleteTemplate(template.id)}
            onSave={() => handleSave(template.id)}
          />
        ))}

        {showAddNew && (
          <AddTemplateForm
            name={newName}
            content={newContent}
            creating={creating}
            onNameChange={setNewName}
            onContentChange={setNewContent}
            onCancel={() => {
              setShowAddNew(false);
              setNewName("");
              setNewContent("");
            }}
            onCreate={() => setCreateConfirmOpen(true)}
            translationLanguageCount={targetLanguageDraft.length}
          />
        )}

        {!showAddNew && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={() => setShowAddNew(true)}
            sx={{ py: 1.25 }}
          >
            {t("settings.addNew")}
          </Button>
        )}
      </Box>
      <Dialog
        open={languageConfirmOpen}
        onClose={() => {
          if (!langSaving) setLanguageConfirmOpen(false);
        }}
        aria-labelledby="language-confirm-dialog-title"
        aria-describedby="language-confirm-dialog-description"
      >
        <DialogTitle id="language-confirm-dialog-title">
          {t("settings.languageConfirm.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="language-confirm-dialog-description"
            component="div"
          >
            <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
              {t("settings.languageConfirm.intro")}
            </Typography>

            {addedLanguageDraft.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  {t("settings.languageConfirm.adding")}
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  {addedLanguageDraft.map(getLanguageName).join(", ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("settings.languageConfirm.addingDescription")}
                </Typography>
              </Box>
            )}

            {removedLanguageDraft.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  {t("settings.languageConfirm.removing")}
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  {removedLanguageDraft.map(getLanguageName).join(", ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("settings.languageConfirm.removingDescription")}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              {t("settings.languageConfirm.moment")}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLanguageConfirmOpen(false)}
            disabled={langSaving}
          >
            {t("settings.cancel")}
          </Button>
          <Button
            onClick={handleSaveLanguages}
            variant="contained"
            disabled={langSaving}
            autoFocus
          >
            {langSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              t("settings.continue")
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={sortConfirmOpen}
        onClose={() => setSortConfirmOpen(false)}
        aria-labelledby="sort-confirm-dialog-title"
        aria-describedby="sort-confirm-dialog-description"
      >
        <DialogTitle id="sort-confirm-dialog-title">
          {t("settings.sortConfirm.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="sort-confirm-dialog-description">
            {t("settings.sortConfirm.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSortConfirmOpen(false)}>
            {t("settings.cancel")}
          </Button>
          <Button
            onClick={applySortAlphabetically}
            variant="contained"
            autoFocus
          >
            {t("settings.continue")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createConfirmOpen}
        onClose={() => {
          if (!creating) setCreateConfirmOpen(false);
        }}
        aria-labelledby="create-confirm-dialog-title"
        aria-describedby="create-confirm-dialog-description"
      >
        <DialogTitle id="create-confirm-dialog-title">
          {t("settings.createConfirm.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="create-confirm-dialog-description">
            {t("settings.createConfirm.description", {
              translationText:
                targetLanguageDraft.length > 0
                  ? t(
                      targetLanguageDraft.length === 1
                        ? "settings.createConfirm.translationText"
                        : "settings.createConfirm.translationTextPlural",
                      { count: targetLanguageDraft.length },
                    )
                  : "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateConfirmOpen(false)}
            disabled={creating}
          >
            {t("settings.cancel")}
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating}
            autoFocus
          >
            {creating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              t("settings.continue")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageSettings;
